import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import apiService from '../../services/apiservice';
import { HOSPITALIZATION_OPTIONS_MAP, HOSPITALIZATION_LIST_4, SUB_SERVICE_LIST } from './enquiryConstants';

export const useAddService = ({ props, roleCode, userObj, shouldShowSeparatePatientNameFields }) => {
  const [loading, setLoading] = useState(false);
  const [rejectBill, setRejectBill] = useState(false);
  const [enqObj, setEnqObj] = useState({});
  const [serviceObj, setServiceObj] = useState({});
  const [serviceList, setServiceList] = useState([]);
  const [procedureList, setProcedureList] = useState([]);
  const [docList, setDocList] = useState([]);
  const [billBlob, setBillBlob] = useState('');
  const [bill, setBill] = useState('');
  const [billType, setBillType] = useState('');
  const [reportBlob, setReportBlob] = useState([]);
  const [viewReport, setViewReport] = useState([]);
  const [showPatientEdit, setShowPatientEdit] = useState(false);
  const [patientObj, setPatientObj] = useState({});

  const formatDate = useCallback((date) => {
    if (!date) return '';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${month}/${day}/${d.getFullYear()}`;
  }, []);

  const convertUTCToLocal = useCallback((date, format = 'MM/DD/YYYY') => {
    return moment.utc(date).local().format(format);
  }, []);

  const parseDate = useCallback((dateString) => {
    if (!dateString) return undefined;
    if (dateString instanceof Date) return dateString;
    if (typeof dateString !== 'string' || !dateString.includes('/')) return undefined;
    const [month, day, year] = dateString.split('/');
    return new Date(year, month - 1, day);
  }, []);

  const encodeBase64File = useCallback((file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
    });
  }, []);

  const getHospitalizationOptions = useCallback(
    () => HOSPITALIZATION_OPTIONS_MAP[+serviceObj?.serviceType] || [],
    [serviceObj?.serviceType]
  );

  const loadService = useCallback(async () => {
    try {
      const res = await apiService.geteServices({ status: 1, serviceCategoryCode: 'CBS-6' });
      if (res.data.responseCode === '200') setServiceList(res.data.successList);
    } catch (error) {
      console.error('Error fetching service list:', error);
    }
  }, []);

  const getFormdata = useCallback(async () => {
    const enquiryId = props.mode === 'Edit' ? props.selected.serviceEnquiryId : props.selected.id;
    const result = await apiService.getEnquiryById({ enquiryId });
    setEnqObj(result.data.successObject);
    return result.data.successObject;
  }, [props.mode, props.selected]);

  const getHospitalizationList = useCallback(
    async (value, enqData) => {
      const ref = enqData || enqObj;
      const payload = {
        serviceType: props.mode === 'Edit' ? props.selected.serviceType : serviceObj?.serviceType || ref?.serviceType,
        hospitalizationTypeId: value || (props.mode === 'Edit' ? props.selected.hospitalizationTypeId : serviceObj?.hospitalizationTypeId || ref?.hospitalizationTypeId),
      };
      const res = await apiService.getHospitaliztionList(payload);
      setProcedureList(res?.data?.data?.length ? res.data.data : []);
    },
    [enqObj, props.mode, props.selected, serviceObj]
  );

  const getDoctorList = useCallback(
    async (surgeryId, enqData) => {
      const ref = enqData || enqObj;
      const payload = {
        hospitalId: props.mode === 'Edit' ? props.selected.hospitalId : ref?.preferredHospitalAndClinics,
        surgeryId: surgeryId || (props.mode === 'Edit' ? props.selected.procedureId : ref?.procedureId),
        type: 'Non-Referral',
        state: props.mode === 'View' ? props.selected.hospitalState : ref?.addressDetails?.state || 'OD',
      };
      if (!payload.hospitalId || !payload.surgeryId || !payload.state) return;
      try {
        const res = await apiService.getMdmUserlist(payload);
        if (res.data.responseCode === '200') {
          setDocList(res.data.successObject.doctorListDTO);
        }
      } catch (error) {
        console.error('Error fetching doctor list:', error);
      }
    },
    [enqObj, props]
  );

  const applyPatientNameFields = useCallback(
    (service) => {
      if (shouldShowSeparatePatientNameFields) {
        const parts = (service?.patientName || '').split(' ');
        service.patientFirstName = parts[0] || '';
        service.patientLastName = parts.slice(1).join(' ') || '';
      } else {
        service.patientName =
          props.mode === 'Edit' || service.billingStatus === 0
            ? service.patientName
            : props.selected?.patientName || service.patientName;
      }
      return service;
    },
    [shouldShowSeparatePatientNameFields, props.mode, props.selected]
  );

  const applyServiceDates = useCallback(
    (service, billingStatus) => {
      const isEditOrBilling0 = props.mode === 'Edit' || billingStatus === 0;
      service.fromDate = isEditOrBilling0 ? formatDate(service.admissionFromDate) : formatDate(props.selected?.procedurePlannedStartDate);
      service.toDate = isEditOrBilling0 ? formatDate(service.admissionToDate) : formatDate(props.selected?.procedurePlannedEndDate);
      service.procedureDate = isEditOrBilling0 ? formatDate(service.procedureDate) : formatDate(props.selected?.procedurePlannedDate);
      return service;
    },
    [props.mode, props.selected, formatDate]
  );

  const normalizeService = useCallback(
    (service, billingStatus) => {
      service.serviceEnquiryId = service.serviceEnquiryId || enqObj?.id || props.selected?.id;
      service = applyPatientNameFields(service);
      service.paymentMode = service.paymentModeCode;
      service.amount = service.totalAmount;
      service.insurence = props.mode === 'Edit' || billingStatus === 0 ? service.typeOfInsurance : '';
      service.transactionid = props.mode === 'Edit' ? service.refTxnId : '';
      service = applyServiceDates(service, billingStatus);
      service.serviceType = props.mode === 'Edit' || billingStatus === 0 ? service.serviceType : props.selected?.serviceType;
      service.serviceSubTypeId = props.mode === 'Edit' || billingStatus === 0 ? service.serviceSubTypeId : props.selected?.serviceSubTypeId;
      service.hospitalizationTypeId = props.mode === 'Edit' || billingStatus === 0 ? service.hospitalizationTypeId : props.selected?.hospitalizationTypeId;
      service.billingRemark = service.billingRemark || props.selected?.billingRemark || '';
      return service;
    },
    [enqObj, props.mode, props.selected, applyPatientNameFields, applyServiceDates]
  );

  const setReportsFromName = useCallback((reportsName) => {
    if (!reportsName) return;
    const reports = reportsName.includes(',') ? reportsName.split(',') : [reportsName];
    setViewReport(reports);
  }, []);

  const applyReferralUserChange = useCallback(
    (name, value) => {
      const doc = docList.find((d) => d.DoctorCode === value);
      if (!doc) return null;
      return {
        [name]: value,
        doctorShare: doc.DoctorFees,
        doctorShareType: doc.DoctorShareType,
        referralUserName: doc.DoctorName,
      };
    },
    [docList]
  );

  const applyHospitalizationTypeChange = useCallback(
    (value) => {
      const base = { hospitalizationTypeId: value, procedureId: '' };
      if (value === enqObj?.hospitalizationTypeId) {
        base.procedureId = enqObj?.procedureId;
      }
      getHospitalizationList(value);
      return base;
    },
    [enqObj, getHospitalizationList]
  );

  const handleChange = useCallback(
    (e) => {
      if (e.preventDefault) e.preventDefault();

      const target = e.target || {};
      const name = target.name;
      const value = e.value === undefined ? target.value : e.value;
      const id = target.id;

      if (value instanceof Date || id === 'date') {
        setServiceObj((prev) => ({ ...prev, [name]: formatDate(value) }));
        return;
      }

      if (name === 'paymentMode') {
        const blockedProcedures = [101, 102, 103, 104, 105, 106];
        if (blockedProcedures.includes(serviceObj.procedureId) && value === 'CBS-0624') {
          toast.warn("You can't select Goverment Insurence right now");
          return;
        }
      }

      if (name === 'referralUserId') {
        const update = applyReferralUserChange(name, value);
        if (update) {
          setServiceObj((prev) => ({ ...prev, ...update }));
          return;
        }
      }

      if (name === 'hospitalizationTypeId') {
        const update = applyHospitalizationTypeChange(value);
        setServiceObj((prev) => ({ ...prev, ...update }));
        return;
      }

      setServiceObj((prev) => ({ ...prev, [name]: value }));
    },
    [formatDate, serviceObj.procedureId, applyReferralUserChange, applyHospitalizationTypeChange]
  );

  const calculatePercentage = useCallback(() => {
    setServiceObj((prev) => {
      const { mci = 0, gross = 0, amount = 0, yieldAmount = 0, doctorShare = 0 } = prev;
      const safe = (num, den) => (den ? ((num / den) * 100).toFixed(2) : '0.00');
      return {
        ...prev,
        mciPercentage: safe(mci, gross),
        gmvPercentage: safe(amount, gross),
        netSalesPercentage: safe(yieldAmount, amount),
        doctorSharePercentage: safe(doctorShare, amount),
      };
    });
  }, []);

  useEffect(() => {
    calculatePercentage();
  }, [serviceObj?.mci, serviceObj?.gross, serviceObj?.amount, serviceObj?.yieldAmount]);

  const openUrl = useCallback(async (fileName) => {
    console.log(fileName)
    const res = await apiService.openFilesBucket(fileName);
    if (res.data.responseCode === '200') window.open(res.data.successObject, '_blank');
  }, []);

  const uploadBill = useCallback(
    async (e) => {
      setBillBlob('');
      const file = e.target.files[0];
      if (file.size > 1024000) {
        toast('please reduce the file size to below 1mb and upload');
        return;
      }
      setBillBlob(URL.createObjectURL(file));
      const b64 = await encodeBase64File(file);
      setBillType(file.type.split('/')[1]);
      const payload = { documentType: file.type.split('/')[1], documentByte: b64, id: serviceObj.id };
      setLoading(true);
      const res = await apiService.updateBill(payload);
      setLoading(false);
      if (res.data.responseCode === '200') {
        toast('Bill uploaded successfully');
        setBill(res.data.successObject);
      }
    },
    [encodeBase64File, serviceObj.id]
  );

  const uploadReport = useCallback(
    async (e) => {
      const payload1 = [];
      for (const file of e.target.files) {
        const b64 = await encodeBase64File(file);
        payload1.push({ documentType: file.type.split('/')[1], documentByte: b64, id: serviceObj.id });
      }
      setLoading(true);
      const res = await apiService.uploadReports(payload1);
      setLoading(false);
      if (res.data.responseCode === '200') {
        toast('Report uploaded successfully');
        setReportsFromName(res.data.successObject);
      }
    },
    [encodeBase64File, serviceObj.id, setReportsFromName]
  );

  const resolveSwasthFields = useCallback(
    (isSwasth) => ({
      swasthUserId: isSwasth ? (props.selected?.swasthUserId || serviceObj.swasthUserId) : null,
      swasthUserName: isSwasth ? (props.selected?.swasthUserName || serviceObj.swasthUserName) : null,
    }),
    [props.selected, serviceObj.swasthUserId, serviceObj.swasthUserName]
  );

  const resolveBillingStatusForRole = useCallback((roleCode) => {
    if (roleCode === 'GROUPPRADMIN') return 2;
    if (roleCode === 'CE') return 1;
    if (roleCode === 'ACCADMIN') return 3;
    return 1;
  }, []);

  const buildTopRecord = useCallback(
    (e, patientName, serviceName) => {
      const isSwasth =
        serviceObj.userRoleCode === 'SWASTH' ||
        props.selected?.rptRoleCode === 'SWASTH' ||
        serviceObj.rptRoleCode === 'SWASTH';

      const sourceChannel = isSwasth ? 'Swasthya Mitra' : 'ECLINIC';

      const swasthFields = resolveSwasthFields(isSwasth);

      const cheCenterId = props.mode === 'Edit' ? props.selected?.cheCenterId : props.selected?.eclinicId;
      const cheCenterName = props.mode === 'Edit' ? props.selected?.cheCenterName : props.selected?.eclinicName;
      const cheBranchId = props.mode === 'Edit' ? props.selected?.cheBranchId : props.selected?.eclinicLocationId;
      const cheBranchName = props.mode === 'Edit' ? props.selected?.cheBranchName : props.selected?.eclinicLocationName;

      const referralUserId = serviceObj?.referral == 1 ? serviceObj?.referralUserId : null;
      const referralUserName = serviceObj?.referral == 1
        ? (docList?.find((d) => d.DoctorCode === serviceObj?.referralUserId)?.DoctorName || '')
        : null;

      const record = {
        patientId: serviceObj.patientId,
        patientName,
        ...(shouldShowSeparatePatientNameFields && {
          patientFirstName: serviceObj.patientFirstName,
          patientLastName: serviceObj.patientLastName,
        }),
        serviceApplicable: 'O',
        userId: '',
        txnId: '',
        serviceCode: 'CHE_BILLING',
        serviceName: 'eClinic billing',
        discount: '',
        discountAmount: '',
        gst: '0',
        discountPecentage: 0,
        totalTaxAmount: 0,
        orderId: props.mode === 'Edit' ? props.selected?.orderId : serviceObj?.orderId || '',
        cheCenterId,
        cheCenterName,
        cheBranchId,
        cheBranchName,
        createdBy: userObj.user.code,
        modifiedBy: userObj.user.code,
        status: 1,
        userCreatedBy: serviceObj.createdBy,
        procedureId: serviceObj.procedureId,
        procedureName: procedureList?.find((p) => p.hospitalizationSubTypeId == serviceObj.procedureId)?.hospitalizationSubType || '',
        typeOfInsurance: serviceObj?.insurence || '',
        yieldAmount: serviceObj.yieldAmount,
        sourceChannel,
        userRoleCode: roleCode,
        ...swasthFields,
        rptRoleCode: props.mode === 'Edit' ? props.selected?.rptRoleCode : (serviceObj.userRoleCode || serviceObj.rptRoleCode),
        serviceStatus: 1,
        documentType: billType,
        refTxnId: serviceObj.transactionid,
        referral: serviceObj?.referral || 0,
        referralUserId,
        referralUserName,
        doctorShare: serviceObj?.doctorShare,
        doctorShareType: serviceObj?.doctorShareType || 'Fixed',
        serviceSubTypeId: serviceObj.serviceSubTypeId || '',
        hospitalizationTypeId: serviceObj.hospitalizationTypeId || '',
        serviceEnquiryId: serviceObj.serviceEnquiryId || enqObj?.id,
        serviceType: serviceObj.serviceType || '',
        mci: serviceObj.mci || 0,
        gross: serviceObj.gross,
        totalAmount: serviceObj.amount,
        curebayFee: serviceObj.amount,
        payMode: 'Partner Payment',
        partnerType: 'Hospital',
        revenueSource: isSwasth ? 'Swasthya Mitra' : 'ECLINIC',
        paymentMode: serviceName,
        paymentModeCode: serviceObj.paymentMode,
        traumaStatus: props.selected?.traumaStatus,
        serviceSubType: SUB_SERVICE_LIST.find((s) => s.value == serviceObj.serviceSubTypeId)?.name,
        hospitalizationType: HOSPITALIZATION_LIST_4.find((h) => h.value == serviceObj.hospitalizationTypeId)?.name,
        facilityId: serviceObj?.hospitalId || '',
        facilityLocId: serviceObj.locationId || '',
        facilityName: serviceObj?.hospitalName || '',
        facilityLocName: serviceObj?.locationName || '',
        hospitalId: serviceObj?.hospitalId || serviceObj?.preferredHospitalAndClinics || '',
        locationId: serviceObj?.locationId || '',
        masterServiceType: `Hospital-${serviceName}`,
        doctorId: serviceObj.referral == 1 ? serviceObj?.referralUserId : null,
        surgeryId: +serviceObj.procedureId,
        state: props.selected?.hospitalState || enqObj?.hospitalState || 'Odisha',
        type: serviceObj.referral == 0 ? 'Referral' : 'Non-Referral',
        billingRemark: serviceObj?.billingRemark || '',
      };

      if (serviceObj.serviceEnquiryId) {
        record.id = serviceObj.id;
        record.reportsName = serviceObj?.reportsName;
      }

      ['procedureDate', 'fromDate', 'toDate'].forEach((field) => {
        let key;
        if (field === 'procedureDate') {
          key = 'procedureDate';
        } else if (field === 'fromDate') {
          key = 'admissionFromDate';
        } else {
          key = 'admissionToDate';
        }
        const src = serviceObj[field];
        record[key] = src ? convertUTCToLocal(new Date(src), 'YYYY-MM-DD HH:mm:ss') : '';
      });

      return record;
    },
    [serviceObj, props, procedureList, docList, enqObj, roleCode, userObj, billType, shouldShowSeparatePatientNameFields, convertUTCToLocal, resolveSwasthFields]
  );

  const validateRoleSpecificFields = useCallback(() => {
    if (serviceObj.referral != 0 && serviceObj.referral != 1) return 'Please Select Referral';
    if (serviceObj.referral == 1 && !serviceObj.referralUserId) return 'Please select the Doctor';
    if (!serviceObj.mci && serviceObj.mci != 0) return 'Please enter mci amount';
    return null;
  }, [serviceObj]);

  const validateService = useCallback(() => {
    if (!serviceObj.hospitalName) return 'Please Select Hospital';
    if (!serviceObj.paymentMode) return 'Please select Payment Mode';
    if (!serviceObj.procedureId) return 'Please select Hospitalization Subtype';
    if (roleCode !== 'CE') {
      const roleError = validateRoleSpecificFields();
      if (roleError) return roleError;
    }
    if (!serviceObj.serviceSubTypeId) return 'Please Select Sub Service Type';
    if (!serviceObj.hospitalizationTypeId) return 'Please Select Hospitalization Type';
    if (!serviceObj.gross) return 'Please enter gross amount';
    if (serviceObj.gross && serviceObj.mci && +serviceObj.gross < +serviceObj.mci) return 'MCI Amount should be less than Gross';
    if (!serviceObj.procedureDate) return 'Please Select Billing Date';
    if (!serviceObj.fromDate) return 'Please Select Admission From Date';
    if (!serviceObj.toDate) return 'Please Select Admission To Date';
    return null;
  }, [serviceObj, roleCode, validateRoleSpecificFields]);

  const applyActionFlagMutations = useCallback(
    (topRecord, actionFlag) => {
      const defaultBillingStatus = resolveBillingStatusForRole(roleCode);

      topRecord.billingStatus = ['C', 'U'].includes(actionFlag)
        ? serviceObj?.billingStatus
        : defaultBillingStatus;
      topRecord.calculationFlag = actionFlag === 'C' ? 1 : 0;

      if (roleCode === 'ACCADMIN') topRecord.billingPublishStatus = 'W';

      if (!((billBlob || props.mode !== 'Edit') && (billBlob || !serviceObj.serviceEnquiryId))) {
        topRecord.uploadBillName = serviceObj.uploadBillName;
        delete topRecord.uploadBill;
      }

      if (serviceObj.reportsName) topRecord.reportsName = serviceObj.reportsName;

      if (actionFlag === 'B') {
        topRecord.billingStatus = defaultBillingStatus;
        if (rejectBill || props.selected.reject) {
          topRecord.billingStatus = 6;
          topRecord.reasonForReject = serviceObj.reasonForReject;
        }
      }
      if (actionFlag === '') topRecord.billingStatus = 1;
      if (actionFlag === 'CS') {
        topRecord.calculationFlag = 1;
        topRecord.billingStatus = serviceObj?.billingStatus || 0;
      }

      return topRecord;
    },
    [roleCode, serviceObj, billBlob, props, rejectBill, resolveBillingStatusForRole]
  );

  const saveService = useCallback(
    async (actionFlag) => {
      const error = validateService();
      if (error) return toast.error(error);

      if (props.mode === 'Edit' && actionFlag === 'R' && !serviceObj.reasonForReject) {
        setRejectBill(true);
        return toast.error('Please enter reason for rejection.');
      }

      const result = serviceList.find((s) => s.code === serviceObj.paymentMode);
      const serviceName = result?.name || '';
      if (!serviceObj.referralUserId) {
        serviceObj.referralUserId = '';
        serviceObj.referralUserName = '';
        serviceObj.doctorShare = '';
      }

      let patientName = serviceObj?.patientName;
      if (shouldShowSeparatePatientNameFields) {
        patientName = [serviceObj.patientFirstName, serviceObj.patientLastName].filter(Boolean).join(' ');
      }

      let topRecord = buildTopRecord(actionFlag, patientName, serviceName);
      topRecord.procedureMode = result?.procedureMode;
      topRecord = applyActionFlagMutations(topRecord, actionFlag);
      delete topRecord.campLead;

      setLoading(true);
      try {
        const res = actionFlag === 'C'
          ? await apiService.calculateService(topRecord)
          : await apiService.saveService(topRecord);
        setLoading(false);

        if (res?.data.responseCode === '200') {
          toast.success(res?.data.responseMessage);
          const payload = { id: res?.data.successObject.id };
          const res1 = await apiService.getServicesBillList(payload);
          setBillBlob('');
          setBillType('');
          setReportBlob([]);
          let service = actionFlag == 'C' ? res?.data.successObject : res1.data.successList[0];
          service = normalizeService(service, topRecord.billingStatus);
          setReportsFromName(service?.reportsName);
          setViewReport([]);
          setServiceObj(service);
          setLoading(false);
        } else {
          toast.error(res?.data?.responseMessage || res?.response?.data?.message || 'Something went wrong');
        }
      } catch (error) {
        console.error('Error saving service:', error);
        toast.error('An unexpected error occurred while saving. Please try again.');
      }
    },
    [
      validateService, props, serviceObj, serviceList, shouldShowSeparatePatientNameFields,
      buildTopRecord, applyActionFlagMutations, billBlob, normalizeService, setReportsFromName,
    ]
  );

  const handleApproval = useCallback(async () => {
    if (props.mode !== 'Edit' && !bill) return toast.error('Please upload the bill.');
    // if (props.mode === 'Edit' && !serviceObj.reasonForReject) {
    //   setRejectBill(true);
    //   return toast.error('Please enter reason for rejection.');
    // }

    const billingStatus = resolveBillingStatusForRole(roleCode);

    const payload = {
      id: serviceObj?.id,
      billingStatus,
      modifiedBy: userObj.user.code,
      userRoleCode: roleCode,
    };
    const res = roleCode === 'ACCADMIN'
      ? await apiService.approvefromFinance(payload)
      : await apiService.apporoveOthers(payload);
    if (res.data.responseCode === '200') {
      setRejectBill(false);
      toast.success(res.data.responseMessage);
      props.onClose();
    } else {
      toast.error(res.data.responseMessage);
    }
  }, [bill, props, serviceObj, roleCode, userObj, resolveBillingStatusForRole]);

  const handleReject = useCallback(async () => {
    if (!serviceObj.reasonForReject) {
      setRejectBill(true);
      return toast.error('Please enter reason for rejection.');
    }
    const payload = {
      id: serviceObj?.id,
      billingStatus: 6,
      reasonForReject: serviceObj.reasonForReject,
      modifiedBy: userObj.user.code,
      userRoleCode: roleCode,
    };
    const res = roleCode === 'ACCADMIN'
      ? await apiService.rejectfromFinance(payload)
      : await apiService.apporoveOthers(payload);
    if (res.data.responseCode === '200') {
      setRejectBill(false);
      toast.success(res.data.responseMessage);
      props.onClose();
    } else {
      toast.error(res.data.responseMessage);
    }
  }, [props, serviceObj, roleCode, userObj]);

  const handleEditPatient = useCallback(async () => {
    setShowPatientEdit(true);
    try {
      const res = await apiService.getPatientName({ patientId: serviceObj.patientId });
      if (res.data.responseCode === '200') {
        setPatientObj(res.data.successObject);
      } else {
        toast.error('Failed to fetch patient details');
      }
    } catch (error) {
      console.error('Error fetching patient details:', error);
      toast.error('Error fetching patient details');
    }
  }, [serviceObj.patientId]);

  const handlePatientUpdate = useCallback(async () => {
    try {
      const payload = {
        modifiedBy: userObj.user.code,
        code: patientObj.code,
        firstName: patientObj.firstName,
        lastName: patientObj.lastName,
      };
      const res = await apiService.patientSave(payload);
      if (res.data.responseCode === '200') {
        toast.success('Patient updated successfully');
        setShowPatientEdit(false);
        const res1 = await apiService.getServicesBillList({ id: serviceObj.id });
        let service = res1.data.successList[0];
        service = normalizeService(service, service.billingStatus);
        setReportsFromName(service?.reportsName);
        setServiceObj(service);
      } else {
        toast.error(res.data.responseMessage || 'Failed to update patient');
      }
    } catch (error) {
      console.error('Error updating patient:', error);
      toast.error('Error updating patient');
    }
  }, [patientObj, userObj, serviceObj.id, normalizeService, setReportsFromName]);

  const initializeService = useCallback(async () => {
    const enqData = await getFormdata();
    await getDoctorList(undefined, enqData);
    await getHospitalizationList(undefined, enqData);
    await loadService();

    let service = props.mode === 'Edit' ? { ...props.selected } : { ...enqData };
    service = applyPatientNameFields(service);

    if (roleCode === 'CE') service.referral = 0;
    service.hospitalName = props.selected?.hospitalName;
    service.procedureId = props.mode === 'Edit' ? +props.selected?.procedureId : enqData?.procedureId;
    service.procedureNameDescription = props.mode === 'Edit' ? props.selected?.procedureNameDescription : enqData?.procedureNameDescription;
    service.paymentMode = props.selected?.paymentModeCode || '';
    service.amount = props.selected?.totalAmount;
    service.insurence = props.mode === 'Edit' ? props.selected?.typeOfInsurance : '';
    service.transactionid = props.mode === 'Edit' ? props.selected?.refTxnId : '';
    service = applyServiceDates(service, service.billingStatus);
    service.serviceType = props.mode === 'Edit' ? props.selected?.serviceType : enqData?.serviceType;
    service.serviceSubTypeId = props.mode === 'Edit' ? props.selected?.serviceSubTypeId : enqData?.serviceSubTypeId;
    service.hospitalizationTypeId = props.mode === 'Edit' ? service.hospitalizationTypeId : enqData?.hospitalizationTypeId;
    service.gross = props.selected?.gross;
    service.mci = props.selected?.mci;
    service.reportsName = props.selected?.reportsName;
    service.procedureDate = formatDate(props.mode === 'Edit' ? props.selected?.procedureDate : new Date());
    service.billingRemark = props.selected?.billingRemark || enqData?.billingRemark || '';

    if (props.mode === 'Edit' && props.selected?.reportsName) {
      setReportsFromName(props.selected.reportsName);
    }

    setServiceObj(service);
  }, [
    getFormdata, getDoctorList, getHospitalizationList, loadService,
    props, roleCode, applyPatientNameFields, applyServiceDates,
    formatDate, setReportsFromName,
  ]);

  useEffect(() => {
    initializeService();
  }, [props.selected]);

  const deleteBlob = useCallback((index) => {
    setReportBlob((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCancel = useCallback(() => {
    if (serviceObj?.billingStatus === 0 && serviceObj?.amount) {
      return toast.error('Please get Approval from Admin');
    }
    props.onClose();
  }, [serviceObj, props]);

  return {
    loading, rejectBill, serviceObj, serviceList, procedureList, docList,
    billBlob, bill, viewReport, reportBlob, showPatientEdit, patientObj,
    parseDate, formatDate,
    setServiceObj, setPatientObj, setShowPatientEdit, setRejectBill,
    getHospitalizationOptions,
    handleChange, saveService, handleApproval, handleReject,
    handleEditPatient, handlePatientUpdate,
    uploadBill, uploadReport, openUrl, deleteBlob, handleCancel,
  };
};